import { z } from 'zod';

const student = z.object({ name: z.string().min(1), dateOfBirth: z.string().nullish(), notes: z.string().nullish() });
const payer = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('existing'), clientId: z.string().min(1), clientName: z.string().optional() }),
  z.object({ mode: z.literal('new'), name: z.string().min(1), zaloPhone: z.string().optional(), email: z.string().optional() }),
]);
const enrollmentBase = z.object({
  subjectId: z.string().min(1), subjectName: z.string(),
  pricingType: z.enum(['PER_SESSION', 'MONTHLY_FLAT']),
  ratePerSession: z.number().positive().optional(),
  // No `chargeAbsent` here on purpose (2026-08-12): a copilot tool must be a
  // bridge to the API and must never be able to do what the API cannot. Every
  // field it writes through its own path (execute.ts writes Prisma directly,
  // not through the API route) is a field that silently goes stale when the
  // API's rules change — which is exactly how this one ended up outdated. A
  // copilot-created enrollment takes the org default like any other.
  billingPeriod: z.enum(['MONTHLY', 'TERM']).optional(),
  termSize: z.number().int().positive().optional(),
  monthlyRate: z.number().positive().optional(),
});
const enrollment = enrollmentBase.refine((v) => v.billingPeriod !== 'TERM' || (v.termSize != null && v.termSize > 0), { message: 'termSize required for TERM', path: ['termSize'] });

export const studentIntakeProposalSchema = z.object({
  student,
  payer,
  enrollment: enrollment.nullish(),
});
export type StudentIntakeProposalInput = z.infer<typeof studentIntakeProposalSchema>;

// Co-pilot batch operation plan (SP2) — reuses the SP1 student/payer/enrollment sub-schemas.
// NOTE: createOp's enrollment uses `enrollmentBase` (no .refine) to avoid nesting a refined
// ZodEffects inside a discriminatedUnion inside an array, which blows the TS instantiation
// depth budget (TS2589) during `tsc --noEmit`.
const createOp = z.object({
  type: z.literal('createStudentEnrollment'),
  student,
  payer,
  enrollment: enrollmentBase.nullish(),
});
const updateEnrollmentOp = z.object({
  type: z.literal('updateEnrollment'),
  enrollmentId: z.string().min(1),
  label: z.string(),
  changes: z.object({
    ratePerSession: z.number().positive().optional(),
    monthlyRate: z.number().positive().optional(),
    // No `chargeAbsent` here (2026-08-12): the copilot must not be able to
    // change the absence rule on an existing enrollment — see the note on
    // `enrollmentBase` above.
    status: z.enum(['ACTIVE', 'PAUSED', 'ENDED']).optional(),
  }),
});
const updateStudentOp = z.object({
  type: z.literal('updateStudent'),
  studentId: z.string().min(1),
  label: z.string(),
  changes: z.object({
    status: z.enum(['ACTIVE', 'INACTIVE']).optional(),
    name: z.string().min(1).optional(),
  }),
});

export const copilotPlanSchema = z.object({
  summary: z.string().min(1),
  // Cap batch size: the /execute endpoint trusts a raw plan (not verified to
  // originate from the agent), so bound it to prevent a single authenticated
  // call from batch-creating unbounded rows in one Serializable transaction.
  ops: z.array(z.discriminatedUnion('type', [createOp, updateEnrollmentOp, updateStudentOp])).min(1).max(50),
});
export type CopilotPlanInput = z.infer<typeof copilotPlanSchema>;

// Shape of the CopilotUndo token produced by ai-engine's execute module's executePlan
// and consumed by undoPlan (SP3: round-tripped through agent graph state, not
// a REST request body — the ai-engine graph is the only caller of undoPlan).
export const copilotUndoSchema = z.object({
  deleteStudentIds: z.array(z.string().min(1)),
  deleteClientIds: z.array(z.string().min(1)),
  restoreEnrollments: z.array(z.object({
    id: z.string().min(1),
    ratePerSession: z.number().nullish(),
    monthlyRate: z.number().nullish(),
    // No `chargeAbsent` here (2026-08-12): the copilot can no longer change it,
    // so there is nothing for undo to restore — see the note on `enrollmentBase` above.
    status: z.enum(['ACTIVE', 'PAUSED', 'ENDED']).optional(),
  })),
  restoreStudents: z.array(z.object({
    id: z.string().min(1),
    status: z.enum(['ACTIVE', 'INACTIVE']).optional(),
    name: z.string().min(1).optional(),
  })),
});
export type CopilotUndoInput = z.infer<typeof copilotUndoSchema>;

// POST /api/copilot/:section request body — section-routed co-pilot chat.
export const copilotMessageSchema = z.object({
  message: z.string().min(1).max(5000),
  threadId: z.string().nullable().optional(),
  reset: z.boolean().optional(),
});
export type CopilotMessageInput = z.infer<typeof copilotMessageSchema>;
